/**
 * Photo import, with no AI and no network.
 *
 * She uploads a picture; this pulls a palette out of it and hands back a brief.
 * It is pure client-side pixel work: draw the image to a canvas, sample it,
 * cluster the colours, snap each cluster to the nearest real DMC floss.
 *
 * This is the primary photo path, not a degraded one. Gemini can add a mood
 * phrase and a style leaning on top if a key is present, but the colours
 * themselves are always derived here, because "which colours are in this
 * photo" is a question with a correct answer that code can compute.
 */
import { FLOSS, nearestFloss, type FlossColor } from '../data/dmc';
import type { DesignBrief } from './types';
import type { Sizing } from '../engine/types';

/** Longest edge the image is scaled to before sampling. Plenty for a palette. */
const SAMPLE_SIZE = 128;

export interface PhotoPalette {
  /** DMC codes, most prominent first. */
  codes: string[];
  /** The raw cluster colours, before snapping, for the "what I saw" strip. */
  swatches: { hex: string; share: number; floss: FlossColor }[];
}

/**
 * k-means over the image's pixels in RGB.
 *
 * Deterministic on purpose: the initial centroids come from an even stride
 * through the pixel list rather than from Math.random, so the same photo always
 * yields the same palette. A photo import that gave different answers each time
 * would break the promise that a design is reproducible.
 */
export function palettteFromPixels(
  pixels: Uint8ClampedArray,
  wanted = 5,
  iterations = 12,
): PhotoPalette {
  const points: [number, number, number][] = [];
  for (let i = 0; i < pixels.length; i += 4) {
    const a = pixels[i + 3]!;
    if (a < 128) continue; // ignore transparent padding
    points.push([pixels[i]!, pixels[i + 1]!, pixels[i + 2]!]);
  }
  if (points.length === 0) {
    return { codes: [], swatches: [] };
  }

  const k = Math.max(1, Math.min(wanted, points.length));
  const stride = Math.max(1, Math.floor(points.length / k));
  let centroids: [number, number, number][] = Array.from(
    { length: k },
    (_, i) => [...points[Math.min(points.length - 1, i * stride)]!] as [number, number, number],
  );

  const assign = new Int32Array(points.length);
  for (let iter = 0; iter < iterations; iter++) {
    let moved = false;
    for (let p = 0; p < points.length; p++) {
      let best = 0;
      let bestD = Number.POSITIVE_INFINITY;
      for (let c = 0; c < centroids.length; c++) {
        const d = sqDist(points[p]!, centroids[c]!);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      if (assign[p] !== best) {
        assign[p] = best;
        moved = true;
      }
    }
    const sums = centroids.map(() => [0, 0, 0, 0]);
    for (let p = 0; p < points.length; p++) {
      const s = sums[assign[p]!]!;
      s[0]! += points[p]![0];
      s[1]! += points[p]![1];
      s[2]! += points[p]![2];
      s[3]! += 1;
    }
    centroids = centroids.map((c, i) => {
      const s = sums[i]!;
      return s[3]! === 0 ? c : [s[0]! / s[3]!, s[1]! / s[3]!, s[2]! / s[3]!];
    });
    if (!moved) break;
  }

  const counts = centroids.map(() => 0);
  for (let p = 0; p < points.length; p++) counts[assign[p]!]! += 1;

  const clusters = centroids
    .map((c, i) => ({
      rgb: c.map((v) => Math.round(v)) as [number, number, number],
      share: counts[i]! / points.length,
    }))
    .filter((c) => c.share > 0)
    .sort((a, b) => b.share - a.share);

  // Snap to floss, dropping duplicates: two clusters that land on the same
  // skein are one colour as far as the shopping list is concerned.
  const codes: string[] = [];
  const swatches: PhotoPalette['swatches'] = [];
  for (const c of clusters) {
    const floss = nearestFloss(c.rgb);
    if (codes.includes(floss.dmc)) continue;
    codes.push(floss.dmc);
    swatches.push({ hex: rgbToHex(c.rgb), share: c.share, floss });
  }

  // If the photo was very flat, top up from the palette so there is something
  // to build a bracelet out of, and be honest about it in the note.
  return { codes, swatches };
}

function sqDist(a: [number, number, number], b: [number, number, number]): number {
  const dr = a[0] - b[0];
  const dg = a[1] - b[1];
  const db = a[2] - b[2];
  return dr * dr + dg * dg + db * db;
}

export function rgbToHex(rgb: [number, number, number]): string {
  return `#${rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Read an image file into a downscaled pixel buffer.
 * Browser-only: uses createImageBitmap and a canvas.
 */
export async function samplePixels(file: Blob): Promise<Uint8ClampedArray> {
  const bitmap = await createImageBitmap(file);
  const scale = SAMPLE_SIZE / Math.max(bitmap.width, bitmap.height);
  const w = Math.max(1, Math.round(bitmap.width * Math.min(1, scale)));
  const h = Math.max(1, Math.round(bitmap.height * Math.min(1, scale)));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser would not give Knotwright a 2D canvas to read the photo with.');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return ctx.getImageData(0, 0, w, h).data;
}

/** Read a file as a data URL, for the preview and for optional Gemini input. */
export function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.readAsDataURL(file);
  });
}

/** Turn a photo into a brief, with no AI involved anywhere. */
export async function interpretPhoto(
  file: Blob,
  options: { sizing: Sizing; text?: string; colorCount?: number },
): Promise<{ brief: DesignBrief; palette: PhotoPalette; dataUrl: string }> {
  const [pixels, dataUrl] = await Promise.all([samplePixels(file), readAsDataUrl(file)]);
  const palette = palettteFromPixels(pixels, options.colorCount ?? 5);

  const codes = palette.codes.length >= 2 ? palette.codes : [...palette.codes];
  // A photo of a single flat colour still has to make a bracelet, so top up
  // with neutrals rather than failing.
  for (const f of FLOSS) {
    if (codes.length >= 3) break;
    if (!codes.includes(f.dmc) && f.family === 'neutral') codes.push(f.dmc);
  }

  const brief: DesignBrief = {
    text: options.text?.trim() || 'colours taken from a photo',
    colors: codes.slice(0, 5),
    stylePreference: 'any',
    motifs: [],
    mood: 'from your photo',
    sizing: options.sizing,
    source: 'photo',
    sourceNote:
      `Knotwright sampled the photo in your browser, grouped it into ${palette.codes.length} main colour${palette.codes.length === 1 ? '' : 's'}, ` +
      'and matched each one to the nearest real DMC floss. No AI and no upload were involved — the photo never left this device.',
  };

  return { brief, palette, dataUrl };
}
